import { useQuery } from "@tanstack/react-query";
import { roleKeys } from "@/lib/query";

export type CapabilityKind = "skill" | "competency";

export interface MatrixRole {
  id: string;
  name: string;
  status: "draft" | "active" | "archived";
}

export interface MatrixCapability {
  /** `<kind>:<id>` — the cell/column identity. */
  key: string;
  id: string;
  name: string;
  kind: CapabilityKind;
  /** Many-to-many as of the category/theme merge — a capability can carry several. */
  categories: { id: string; name: string }[];
}

export interface MatrixCategoryAxisEntry {
  id: string;
  name: string;
}

/**
 * How much of a role's requirement set the category view can speak for,
 * broken out by kind. `categorizedSkills`/`categorizedCompetencies` at 0
 * alongside `skills`/`competencies` above 0 is the incomplete case — the role
 * asks for things, none are filed under a category yet, and that must never
 * read as "this role overlaps with nothing".
 */
export interface RoleCategoryCoverage {
  roleId: string;
  skills: number;
  categorizedSkills: number;
  competencies: number;
  categorizedCompetencies: number;
}

export interface RoleCapabilityMatrix {
  roles: MatrixRole[];
  capabilities: MatrixCapability[];
  required: { roleId: string; key: string }[];
  categories: MatrixCategoryAxisEntry[];
  categoryRequired: { roleId: string; categoryId: string; count: number }[];
  coverage: RoleCategoryCoverage[];
}

export function useRoleMatrix() {
  return useQuery({
    queryKey: roleKeys.matrix,
    queryFn: async (): Promise<RoleCapabilityMatrix> => {
      const res = await fetch("/api/roles/matrix");
      if (!res.ok) throw new Error("Failed to fetch the role capability matrix");
      const json = await res.json();
      return json.data;
    },
  });
}
